import type { JSX, ReactNode } from 'react';
import clsx from 'clsx';

export interface EntityStatWidgetProps {
  label: string;
  value: ReactNode;
  description?: ReactNode;
  icon?: ReactNode;
  tone?: 'default' | 'success' | 'warning' | 'danger';
  className?: string;
}

/**
 * EntityStatWidget displays a compact summary stat with optional iconography and contextual tone.
 */
export function EntityStatWidget({
  label,
  value,
  description,
  icon,
  tone = 'default',
  className,
}: EntityStatWidgetProps): JSX.Element {
  return (
    <article
      className={clsx(
        'flex items-center gap-md rounded-2xl border bg-white p-5 shadow-sm transition',
        toneClassName(tone),
        className,
      )}
    >
      {icon != null ? <div className="text-2xl text-current">{icon}</div> : null}
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-wide text-text-primary/60">
          {label}
        </p>
        <div className="text-2xl font-semibold text-brand-primary">{value}</div>
        {description != null && <p className="text-sm text-text-primary/70">{description}</p>}
      </div>
    </article>
  );
}

function toneClassName(tone: EntityStatWidgetProps['tone']): string | undefined {
  switch (tone) {
    case 'success':
      return 'border-emerald-200 bg-emerald-50 text-emerald-700';
    case 'warning':
      return 'border-amber-200 bg-amber-50 text-amber-700';
    case 'danger':
      return 'border-red-200 bg-red-50 text-red-700';
    default:
      return 'border-surface-subtle text-text-primary';
  }
}
